import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
import OAuthCallback from './pages/OAuthCallback'
import Dashboard from './pages/Dashboard'
import Pages from './pages/Pages'
import PageBuilder from './pages/PageBuilder'
import PagePreview from './pages/PagePreview'
import BlockTypes from './pages/BlockTypes'
import EditBlockType from './pages/EditBlockType'
import Languages from './pages/Languages'
import Navigations from './pages/Navigations'
import NavigationBuilder from './pages/NavigationBuilder'
import Settings from './pages/Settings'
import Posts from './pages/Posts'
import PostBuilder from './pages/PostBuilder'
import Categories from './pages/Categories'
import MediaLibrary from './pages/MediaLibrary'
import Forms from './pages/Forms'
import FormBuilder from './pages/FormBuilder'
import FormSubmissions from './pages/FormSubmissions'
import Users from './pages/Users'
import UserEdit from './pages/UserEdit'
import MenuPermissions from './pages/MenuPermissions'
import Leads from './pages/Leads'
import LeadDetail from './pages/LeadDetail'
import LeadLog from './pages/LeadLog'
import Maintenance from './pages/Maintenance'
import MaintenanceDetail from './pages/MaintenanceDetail'
import { MailReviewAccessRoute } from './components/MailReviewAccess'

const Products = lazy(() => import('./pages/Products'))
const ProductBuilder = lazy(() => import('./pages/ProductBuilder'))
const Templates = lazy(() => import('./pages/Templates'))
const TemplateBuilder = lazy(() => import('./pages/TemplateBuilder'))
const BlockPresets = lazy(() => import('./pages/BlockPresets'))
const BlockPresetBuilder = lazy(() => import('./pages/BlockPresetBuilder'))
const Orders = lazy(() => import('./pages/Orders'))
const OrderDetail = lazy(() => import('./pages/OrderDetail'))
const Customers = lazy(() => import('./pages/Customers'))
const Discounts = lazy(() => import('./pages/Discounts'))
const DiscountEditor = lazy(() => import('./pages/DiscountEditor'))
const Coupons = lazy(() => import('./pages/Coupons'))
const CouponEditor = lazy(() => import('./pages/CouponEditor'))
const MailReview = lazy(() => import('./pages/MailReview'))

function App() {
  const { isAuthenticated, user } = useAuthStore()
  const ecommerceEnabled = !!user?.features?.ecommerce_enabled

  const renderLazy = (Component) => (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  )

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      {isAuthenticated ? (
        <Route path="/" element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/pages"
            element={
              <ProtectedRoute path="/pages">
                <Pages />
              </ProtectedRoute>
            }
          />
          <Route path="/pages/:id/builder" element={<PageBuilder />} />
          <Route path="/pages/:id/preview" element={<PagePreview />} />
          <Route
            path="/posts"
            element={
              <ProtectedRoute path="/posts">
                <Posts />
              </ProtectedRoute>
            }
          />
          <Route path="/posts/:id" element={<PostBuilder />} />
          <Route path="/posts/:id/preview" element={<PagePreview />} />
          <Route
            path="/categories"
            element={
              <ProtectedRoute path="/categories">
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/block-types"
            element={
              <ProtectedRoute path="/block-types">
                <BlockTypes />
              </ProtectedRoute>
            }
          />
          <Route path="/block-types/:id/edit" element={<EditBlockType />} />
          <Route
            path="/languages"
            element={
              <ProtectedRoute path="/languages">
                <Languages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/navigations"
            element={
              <ProtectedRoute path="/navigations">
                <Navigations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/navigations/:slug/builder"
            element={<NavigationBuilder />}
          />
          <Route
            path="/media"
            element={
              <ProtectedRoute path="/media">
                <MediaLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms"
            element={
              <ProtectedRoute path="/media">
                <Forms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute path="/leads">
                <Leads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lead-log"
            element={
              <ProtectedRoute path="/lead-log">
                <LeadLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <ProtectedRoute path="/leads">
                <LeadDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/maintenance"
            element={
              <ProtectedRoute path="/maintenance">
                <Maintenance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mail-review"
            element={
              <MailReviewAccessRoute>
                {renderLazy(MailReview)}
              </MailReviewAccessRoute>
            }
          />
          <Route
            path="/maintenance/:id"
            element={
              <ProtectedRoute path="/maintenance">
                <MaintenanceDetail />
              </ProtectedRoute>
            }
          />
          <Route path="/forms/:id/edit" element={<FormBuilder />} />
          <Route path="/forms/:id/submissions" element={<FormSubmissions />} />
          <Route
            path="/templates"
            element={
              <ProtectedRoute path="/templates">
                {renderLazy(Templates)}
              </ProtectedRoute>
            }
          />
          <Route path="/templates/new" element={renderLazy(TemplateBuilder)} />
          <Route path="/templates/:id" element={renderLazy(TemplateBuilder)} />
          <Route
            path="/block-presets"
            element={
              <ProtectedRoute path="/templates">
                {renderLazy(BlockPresets)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/block-presets/new"
            element={
              <ProtectedRoute path="/templates">
                {renderLazy(BlockPresetBuilder)}
              </ProtectedRoute>
            }
          />
          <Route
            path="/block-presets/:id"
            element={
              <ProtectedRoute path="/templates">
                {renderLazy(BlockPresetBuilder)}
              </ProtectedRoute>
            }
          />
          {ecommerceEnabled && (
            <>
              <Route
                path="/products"
                element={
                  <ProtectedRoute path="/products">
                    {renderLazy(Products)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products/new"
                element={renderLazy(ProductBuilder)}
              />
              <Route
                path="/products/:id"
                element={renderLazy(ProductBuilder)}
              />
              <Route
                path="/products/:id/edit"
                element={renderLazy(ProductBuilder)}
              />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute path="/orders">
                    {renderLazy(Orders)}
                  </ProtectedRoute>
                }
              />
              <Route path="/orders/:id" element={renderLazy(OrderDetail)} />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute path="/customers">
                    {renderLazy(Customers)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/discounts"
                element={
                  <ProtectedRoute path="/discounts">
                    {renderLazy(Discounts)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/discounts/new"
                element={renderLazy(DiscountEditor)}
              />
              <Route
                path="/discounts/:id/edit"
                element={renderLazy(DiscountEditor)}
              />
              <Route
                path="/coupons"
                element={
                  <ProtectedRoute path="/coupons">
                    {renderLazy(Coupons)}
                  </ProtectedRoute>
                }
              />
              <Route path="/coupons/new" element={renderLazy(CouponEditor)} />
              <Route
                path="/coupons/:id/edit"
                element={renderLazy(CouponEditor)}
              />
            </>
          )}
          <Route
            path="/users"
            element={
              <ProtectedRoute path="/users">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route path="/users/new" element={<UserEdit />} />
          <Route path="/users/:id/edit" element={<UserEdit />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute path="/settings">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route path="/menu-permissions" element={<MenuPermissions />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  )
}

export default App
